import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { Modal } from "./ui";
import { Upload, Check, AlertTriangle } from "lucide-react";

/** Excel import flow for Induction attendees: select file → preview → confirm. */
export default function ImportInductionAttendees({ inductionId, open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  function reset() {
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    const form = new FormData();
    form.append("file", file);
    const token = localStorage.getItem("token");
    const audit = localStorage.getItem("audit_tenant_id");
    const headers = { Authorization: `Bearer ${token}` };
    if (audit) headers["X-Audit-Tenant-Id"] = audit;
    try {
      const res = await fetch(`${BACKEND_URL}/api/inductions/${inductionId}/import-attendees/preview`, {
        method: "POST",
        headers,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert("Error: " + (err.detail || res.statusText));
        return;
      }
      setPreview(await res.json());
    } finally { setUploading(false); }
  }

  async function confirm() {
    setConfirming(true);
    try {
      const { data } = await api.post(`/inductions/${inductionId}/import-attendees/confirm`, {
        rows: preview.preview,
      });
      alert(`✅ Se importaron ${data.inserted} asistentes.`);
      if (onImported) onImported(data);
      reset();
      onClose();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setConfirming(false); }
  }

  function updateRow(idx, field, value) {
    const copy = { ...preview };
    copy.preview[idx][field] = value;
    // recompute warnings for this row
    const r = copy.preview[idx];
    const warnings = [];
    if (!r.file_id) warnings.push("Falta ficha");
    if (!r.name) warnings.push("Falta nombre");
    r.warnings = warnings;
    r.has_warnings = warnings.length > 0;
    copy.rows_with_warnings = copy.preview.filter((p) => p.has_warnings).length;
    setPreview(copy);
  }

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Importar asistentes desde Excel" size="xl" testId="import-excel-modal">
      <div className="grid gap-3">
        <div className="card" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid var(--info)" }}>
          <div className="text-sm">
            📋 <b>Columnas esperadas en el Excel:</b> <code># Ficha</code>, <code>Nombre completo</code>, <code>Patio/Área</code>, <code>Contrato/Departamento</code>.
          </div>
          <div className="text-xs text-dim mt-2">El sistema reconoce variantes en mayúsculas/minúsculas. Las filas incompletas se marcan en amarillo para que las completes antes de confirmar.</div>
        </div>

        <div>
          <label className="label">Archivo Excel (.xlsx)</label>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="input" onChange={(e) => handleFile(e.target.files[0])} data-testid="excel-file-input" />
        </div>

        {uploading && <div className="text-dim text-sm flex items-center gap-2"><span className="spinner" /> Analizando Excel...</div>}

        {preview && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="badge badge-green"><Check size={12} /> {preview.total_rows} filas detectadas</span>
              {preview.rows_with_warnings > 0 && (
                <span className="badge badge-yellow"><AlertTriangle size={12} /> {preview.rows_with_warnings} con datos faltantes</span>
              )}
              <span className="text-xs text-dim">Columnas: {preview.columns_detected.join(", ")}</span>
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto" }} className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>#</th><th># Ficha</th><th>Nombre</th><th>Patio/Área</th><th>Contrato/Dpto</th><th>Aviso</th></tr>
                </thead>
                <tbody>
                  {preview.preview.map((r, i) => (
                    <tr key={i} style={{ background: r.has_warnings ? "rgba(243,156,18,0.10)" : "transparent" }} data-testid={`import-row-${i}`}>
                      <td>{i + 1}</td>
                      <td><input className="input" style={{ padding: 4 }} value={r.file_id} onChange={(e) => updateRow(i, "file_id", e.target.value)} /></td>
                      <td><input className="input" style={{ padding: 4 }} value={r.name} onChange={(e) => updateRow(i, "name", e.target.value)} /></td>
                      <td><input className="input" style={{ padding: 4 }} value={r.area} onChange={(e) => updateRow(i, "area", e.target.value)} /></td>
                      <td><input className="input" style={{ padding: 4 }} value={r.department} onChange={(e) => updateRow(i, "department", e.target.value)} /></td>
                      <td className="text-xs text-warn">{r.warnings.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn" onClick={() => reset()}>Cancelar / Recargar</button>
              <button className="btn btn-primary" onClick={confirm} disabled={confirming} data-testid="confirm-import-btn">
                {confirming ? <span className="spinner" /> : <><Upload size={14} /> Confirmar e importar {preview.total_rows} asistentes</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
